import {Component, Input, OnInit} from "@angular/core";
import {FormControl} from "@angular/forms";
import {Product} from "../product";
import {Website} from "../website";
import {DialogComponent} from "../../../../ng2-bootstrap-modal/dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {UserSubscription, UserSubscriptionCategory, UserSubscriptionType} from "../../../user/user-subscription";
import {Subscription} from "rxjs";
import {IPlanDescriptionInstance} from "../../../user/plan-description/plan-description";
import {PaymentDialogComponent} from "../../../workspace/billing/payment-dialog/payment-dialog.component";
import {Config} from '../../../config/config';

export interface IPremiumPluginData {
  website: Website;
  product: Product;
  fromExtension: boolean;
  isManagable: boolean;
  userSubscriptions: UserSubscription[];
  currentSubscription: UserSubscription;
  color: any;
}

@Component({
    selector: 'db-premium-plugin-dialog',
    templateUrl: './premium-plugin-dialog.component.html',
    styleUrls: ['./premium-plugin-dialog.component.scss'],
    standalone: false
})
export class PremiumPluginDialogComponent extends DialogComponent<IPremiumPluginData, null> implements OnInit, IPremiumPluginData {

  @Input() fromDialog?: boolean;
  @Input() website: Website;
  @Input() product: Product;
  @Input() fromExtension: boolean;
  @Input() isManagable: boolean;
  @Input() userSubscriptions: UserSubscription[];
  @Input() currentSubscription: UserSubscription;
  @Input() color: any;

  public UserSubscriptionType: any = UserSubscriptionType;
  public UserSubscriptionCategory: any = UserSubscriptionCategory;

  public toDialog: boolean;
  public periodSwitch: FormControl;
  public domainsSwitch: FormControl;
  public hasDomainChoice: boolean;

  private _subscriptions: Subscription[];

  public upgradeBasicSubscription: UserSubscription;
  public upgradeStandardSubscription: UserSubscription;
  public upgradeAdvancedSubscription: UserSubscription;
  public upgradeSpecialOfferSubscription: UserSubscription;
  public oneProductPlanDescriptionInstance: IPlanDescriptionInstance;
  public upgradeBundleSubscription: UserSubscription;
  public specialPlanDescriptionInstance: IPlanDescriptionInstance;

  public oneTimePayment: boolean;
  public showPlanItemOneProduct: boolean;
  public showPlanItemBundle: boolean;

  public isPublishedSinglePlugin: boolean;


  constructor(
    private _dialogService: DialogService
  ) {
    super(
      _dialogService
    );
  }

  ngOnInit() {
    this.isPublishedSinglePlugin = Config.publishedSinglePluginPlans.includes(this.product.slug);

    this.oneTimePayment = false;
    this.hasDomainChoice = !(this.currentSubscription.type === UserSubscriptionType.PREMIUM && this.currentSubscription.plan && this.currentSubscription.plan.domainCount > 1);
    this.showPlanItemOneProduct = !this.fromExtension && this.isPublishedSinglePlugin && this.currentSubscription.type === UserSubscriptionType.FREE ? true : false;
    this.showPlanItemBundle = true; /*this.currentSubscription.type === UserSubscriptionType.ONE_PRODUCT ? this.currentSubscription.productSlug !== this.product.slug : true;*/

    this.initFilters();
    this.determineUpgradeSubscription();

    this.addEventHandlers();
  }

  override ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private initFilters(): void {
    this.periodSwitch = new FormControl(this.currentSubscription.plan.periodNumber === 1 ? 6 : this.currentSubscription.plan.periodNumber);

    let initialDomainsCount: number;
    if (this.currentSubscription.type === UserSubscriptionType.PREMIUM) {
      if (this.currentSubscription.plan.domainCount < 3) {
        initialDomainsCount = 3;
      }
      else if (this.currentSubscription.plan.domainCount < 30) {
        initialDomainsCount = 30;
      }
    }
    else {
      initialDomainsCount = this.currentSubscription.plan.domainCount;
    }
    this.domainsSwitch = new FormControl(initialDomainsCount);
  }


  private addEventHandlers(): void {
    this._subscriptions = [];

    this._subscriptions.push(this.periodSwitch.valueChanges.subscribe((): void => {
      this.determineUpgradeSubscription();
    }));

    this._subscriptions.push(this.domainsSwitch.valueChanges.subscribe((): void => {
      this.determineUpgradeSubscription();
    }));
  }

  private determineUpgradeSubscription(): void {
    this.userSubscriptions.map((userSubscription: UserSubscription) => {
       if (userSubscription && userSubscription.plan && userSubscription.plan.type === UserSubscriptionType.ONE_PRODUCT
        && userSubscription.plan.description.description.premiumPluginKey === this.product.slug
        && userSubscription.plan.periodNumber === 6
        && userSubscription.plan.domainCount === 1) {
         this.upgradeBasicSubscription = userSubscription;
       }
       else if (userSubscription && userSubscription.plan && userSubscription.plan.type === UserSubscriptionType.ONE_PRODUCT
        && userSubscription.plan.description.description.premiumPluginKey === this.product.slug
        && userSubscription.plan.periodNumber === 12
        && userSubscription.plan.domainCount === 3) {
         this.upgradeStandardSubscription = userSubscription;
       }
       else if (userSubscription && userSubscription.plan && userSubscription.plan.type === UserSubscriptionType.ONE_PRODUCT
        && userSubscription.plan.description.description.premiumPluginKey === this.product.slug
        && userSubscription.plan.periodNumber === 12
        && userSubscription.plan.domainCount === 30) {
         this.upgradeAdvancedSubscription = userSubscription;
       }
       else if (userSubscription && userSubscription.plan && userSubscription.plan.type === UserSubscriptionType.PREMIUM
        && userSubscription.plan.periodNumber === 6
        && userSubscription.plan.domainCount === 30) {
         this.upgradeSpecialOfferSubscription = userSubscription;
       }
    });
    this.oneProductPlanDescriptionInstance = this.upgradeStandardSubscription ? this.upgradeStandardSubscription.plan.description.getFeatures(this.product.slug) : null;

    this.upgradeBundleSubscription = this.userSubscriptions.find((userSubscription: UserSubscription): boolean => {
      return userSubscription && userSubscription.plan && userSubscription.plan.type === UserSubscriptionType.PREMIUM && userSubscription.plan.periodNumber === this.periodSwitch.value && userSubscription.plan.domainCount === this.domainsSwitch.value;
    });
    if (this.isPublishedSinglePlugin) {
      this.specialPlanDescriptionInstance = this.upgradeSpecialOfferSubscription ? this.upgradeSpecialOfferSubscription.plan.description.getFeatures(this.product.slug) : null;
    }
    else {
      this.specialPlanDescriptionInstance = this.upgradeSpecialOfferSubscription ? this.upgradeBundleSubscription.plan.description.getComparisonFeatures() : null;
    }
  }


  private removeEventHandlers(): void {
    this._subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this._subscriptions = null;
  }

  public upgradePlan(upgradeSubscription: UserSubscription): void {
    this.toDialog = true;

    this.close();
    this._dialogService
      .addDialog(PaymentDialogComponent, {
        upgradeSubscription: upgradeSubscription,
        fromDialog: true
      })
      .subscribe((reopenLast: boolean): void => {
        if (reopenLast) {
          this._dialogService
            .addDialog(PremiumPluginDialogComponent, {
              website: this.website,
              product: this.product,
              isManagable: this.isManagable,
              userSubscriptions: this.userSubscriptions,
              currentSubscription: this.currentSubscription,
              fromExtension: false,
              color: undefined
            });
        }
      });
  }

}
